/**
 * Bachgold — Klaviyo Back-in-Stock Modal Controller
 *
 * Triggers the global Bachgold-BIS-Modal from sold-out PDP buttons,
 * captures email + variant context, and POSTs directly to Klaviyo's
 * Back-in-Stock-Subscriptions endpoint.
 *
 * Klaviyo handles the rest: profile creation, variant subscription,
 * trigger when inventory restocks (via Shopify-integration sync).
 *
 * Endpoint: POST /client/back-in-stock-subscriptions/?company_id=PUBLIC_KEY
 * Variant-ID format for Shopify: $shopify:::$default:::{variant_id}
 */
(function () {
  'use strict';

  const MODAL_ID = 'BachgoldBisModal';
  const KLAVIYO_API_REVISION = '2024-10-15';
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

  const config = window.BachgoldBisConfig || {};
  const PUBLIC_KEY = config.publicKey || '';

  let lastFocusedElement = null;
  let activeContext = null;

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function setState(modal, state) {
    const states = modal.querySelectorAll('[data-bg-bis-state]');
    states.forEach(function (el) {
      el.hidden = el.getAttribute('data-bg-bis-state') !== state;
    });
  }

  function extractContext(source) {
    if (source && source.dataset) {
      return {
        productId: source.dataset.productId || '',
        productHandle: source.dataset.productHandle || '',
        productTitle: source.dataset.productTitle || '',
        productUrl: source.dataset.productUrl || '',
        productImage: source.dataset.productImage || '',
        variantId: source.dataset.variantId || '',
        variantTitle: source.dataset.variantTitle || '',
      };
    }
    if (source && typeof source === 'object') {
      return {
        productId: source.productId || '',
        productHandle: source.productHandle || '',
        productTitle: source.productTitle || '',
        productUrl: source.productUrl || '',
        productImage: source.productImage || '',
        variantId: source.variantId || '',
        variantTitle: source.variantTitle || '',
      };
    }
    return null;
  }

  function openModal(source) {
    const modal = getModal();
    if (!modal) return;

    const ctx = extractContext(source);
    if (!ctx) return;
    activeContext = ctx;

    const productLabel = $('[data-bg-bis-product-name]', modal);
    if (productLabel) {
      const variantSuffix = activeContext.variantTitle && activeContext.variantTitle !== 'Default Title'
        ? ' · ' + activeContext.variantTitle
        : '';
      productLabel.textContent = activeContext.productTitle + variantSuffix;
    }

    // Dynamische Eyebrow + Headline mit Variant-Name (stärker als generisch)
    const eyebrow = $('[data-bg-bis-eyebrow]', modal);
    if (eyebrow && activeContext.variantTitle && activeContext.variantTitle !== 'Default Title') {
      eyebrow.textContent = activeContext.variantTitle + ' gerade ausverkauft';
    }

    renderAlternatives(modal);

    setState(modal, 'form');
    resetForm(modal);

    modal.hidden = false;
    requestAnimationFrame(function () {
      modal.setAttribute('data-bg-bis-open', '');
      modal.setAttribute('aria-hidden', 'false');
    });

    lastFocusedElement = document.activeElement;
    const emailInput = $('#BachgoldBisEmail', modal);
    if (emailInput) {
      setTimeout(function () { emailInput.focus(); }, 80);
    }

    document.addEventListener('keydown', onKeydown);
  }

  function closeModal() {
    const modal = getModal();
    if (!modal) return;

    modal.removeAttribute('data-bg-bis-open');
    modal.setAttribute('aria-hidden', 'true');

    setTimeout(function () {
      modal.hidden = true;
    }, 220);

    document.removeEventListener('keydown', onKeydown);
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
    activeContext = null;
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      closeModal();
    }
  }

  function resetForm(modal) {
    const form = $('[data-bg-bis-form]', modal);
    if (!form) return;
    form.reset();

    const error = $('[data-bg-bis-error]', modal);
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    const input = $('#BachgoldBisEmail', modal);
    if (input) input.removeAttribute('aria-invalid');

    setLoading(modal, false);
  }

  function setLoading(modal, isLoading) {
    const submit = $('[data-bg-bis-form] button[type="submit"]', modal);
    const label = $('[data-bg-bis-submit-label]', modal);
    const spinner = $('[data-bg-bis-spinner]', modal);

    if (submit) submit.disabled = isLoading;
    if (label) label.style.opacity = isLoading ? '0.5' : '1';
    if (spinner) spinner.hidden = !isLoading;
  }

  function showError(modal, message) {
    const error = $('[data-bg-bis-error]', modal);
    const input = $('#BachgoldBisEmail', modal);
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    if (input) input.setAttribute('aria-invalid', 'true');
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function readVariantsFromPdp() {
    const script = document.querySelector('[data-bachgold-variants]');
    if (!script) return [];
    try {
      const arr = JSON.parse(script.textContent);
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }

  function isVariantPurchasable(v) {
    if (!v) return false;
    if (v.available === true) return true;
    if (v.inventory_management === 'shopify'
        && v.inventory_quantity != null
        && v.inventory_quantity > 0) return true;
    return false;
  }

  function buildAlternativeItem(variant) {
    const li = document.createElement('li');
    li.className = 'bg-bis-modal__alt-item';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bg-bis-modal__alt-btn';
    btn.setAttribute('data-bg-bis-alt', String(variant.id));

    if (variant.featured_image && variant.featured_image.src) {
      const img = document.createElement('img');
      img.src = 'https:' + variant.featured_image.src + '&width=80';
      img.alt = variant.title || '';
      img.loading = 'lazy';
      img.className = 'bg-bis-modal__alt-img';
      btn.appendChild(img);
    } else {
      const swatch = document.createElement('span');
      swatch.className = 'bg-bis-modal__alt-swatch';
      btn.appendChild(swatch);
    }

    const label = document.createElement('span');
    label.className = 'bg-bis-modal__alt-label';
    label.textContent = variant.title && variant.title !== 'Default Title'
      ? variant.title
      : 'Variante';
    btn.appendChild(label);

    const arrow = document.createElement('span');
    arrow.className = 'bg-bis-modal__alt-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>';
    btn.appendChild(arrow);

    li.appendChild(btn);
    return li;
  }

  function renderAlternatives(modal) {
    if (!modal || !activeContext) return;
    const variants = readVariantsFromPdp();
    if (!variants.length) return;

    const currentVariantId = parseInt(activeContext.variantId, 10);
    const available = variants.filter(function (v) {
      return isVariantPurchasable(v) && v.id !== currentVariantId;
    }).slice(0, 4);

    [
      { listSel: '[data-bg-bis-alt-list]', wrapSel: '[data-bg-bis-alternatives]' },
      { listSel: '[data-bg-bis-alt-list-success]', wrapSel: '[data-bg-bis-alternatives-success]' },
    ].forEach(function (target) {
      const list = modal.querySelector(target.listSel);
      const wrap = modal.querySelector(target.wrapSel);
      if (!list || !wrap) return;
      list.innerHTML = '';
      if (!available.length) {
        wrap.hidden = true;
        return;
      }
      available.forEach(function (v) { list.appendChild(buildAlternativeItem(v)); });
      wrap.hidden = false;
    });
  }

  function subscribeBackInStock(email) {
    if (!PUBLIC_KEY || !activeContext || !activeContext.variantId) {
      return Promise.reject(new Error('missing-config'));
    }

    const variantCatalogId = '$shopify:::$default:::' + activeContext.variantId;

    const body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email },
            },
          },
        },
        relationships: {
          variant: {
            data: { type: 'catalog-variant', id: variantCatalogId },
          },
        },
      },
    };

    return fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(PUBLIC_KEY), {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: KLAVIYO_API_REVISION,
      },
      body: JSON.stringify(body),
    }).then(function (response) {
      if (response.status === 202 || response.ok) {
        return true;
      }
      return response.json().then(function (data) {
        const detail = data && data.errors && data.errors[0] && data.errors[0].detail;
        throw new Error(detail || 'klaviyo-error-' + response.status);
      });
    });
  }

  function onSubmit(event) {
    event.preventDefault();
    const modal = getModal();
    if (!modal) return;

    const input = $('#BachgoldBisEmail', modal);
    const email = input ? input.value.trim() : '';

    if (!isValidEmail(email)) {
      showError(modal, 'Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }

    showError(modal, '');
    const error = $('[data-bg-bis-error]', modal);
    if (error) error.hidden = true;

    setLoading(modal, true);

    if (window.klaviyo && typeof window.klaviyo.push === 'function' && activeContext) {
      try {
        window.klaviyo.push(['identify', { $email: email }]);
      } catch (e) { /* noop */ }
    }

    subscribeBackInStock(email)
      .then(function () {
        setState(modal, 'success');
      })
      .catch(function (err) {
        setLoading(modal, false);
        const message = err && err.message === 'missing-config'
          ? 'Konfiguration unvollständig. Bitte später erneut versuchen.'
          : 'Anmeldung fehlgeschlagen. Bitte später erneut versuchen.';
        showError(modal, message);
      });
  }

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('.bachgold-klaviyo-bis-trigger');
    if (trigger) {
      event.preventDefault();
      openModal(trigger);
      return;
    }

    const altLink = event.target.closest('[data-bg-bis-alt]');
    if (altLink) {
      // Cross-sell: User klickt verfügbare Alternative im Success/Form-State
      event.preventDefault();
      const variantId = altLink.getAttribute('data-bg-bis-alt');
      if (variantId) {
        // Update PDP-side variant via change event on hidden input
        const idInputs = document.querySelectorAll('[data-variant-id-input], [data-sticky-variant-id]');
        idInputs.forEach(function (inp) {
          inp.value = variantId;
          inp.dispatchEvent(new Event('change', { bubbles: true }));
        });
        closeModal();
      }
      return;
    }

    const closer = event.target.closest('[data-bg-bis-close]');
    if (closer) {
      event.preventDefault();
      closeModal();
    }
  });

  document.addEventListener('submit', function (event) {
    if (event.target && event.target.matches('[data-bg-bis-form]')) {
      onSubmit(event);
    }
  });

  // Public API — programmatic modal trigger from other scripts (e.g. variant-swap)
  window.BachgoldBis = window.BachgoldBis || {};
  window.BachgoldBis.open = function (data) {
    openModal(data);
  };
  window.BachgoldBis.close = closeModal;
})();
